import { useMemo, useState } from "react";
import { mixExplosiveLimits } from "../calc/explosiveLimits";
import { moleFractionsToMassComponents } from "../calc/gasComposition";
import { GAS_COMPONENTS } from "../data/gasComponents";
import { Field, Item, num } from "./formBits";

interface ComponentEntry {
  readonly id: string;
  readonly key: string;
  readonly molePercent: string;
}

/** 초기값은 KOSHA GUIDE P-179-2022 <부록 5> 예제 조성이다. */
const INITIAL_ENTRIES: readonly ComponentEntry[] = [
  { id: "el_0", key: "hydrogen", molePercent: "2" },
  { id: "el_1", key: "methane", molePercent: "6" },
  { id: "el_2", key: "argon", molePercent: "27" },
  { id: "el_3", key: "helium", molePercent: "65" },
];

function fmt(v: number, digits = 2): string {
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("ko-KR", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** vol% 는 정의상 100을 넘을 수 없다 — 계산값이 100 이상이면 "이 조성으로는
 * 어떤 비율로 섞어도 그 한계에 도달할 수 없다"는 뜻이므로 그렇게 표시한다
 * (불연성 가스로 심하게 희석된 혼합물에서 나타난다). */
function fmtLimit(v: number, digits = 3): string {
  if (!Number.isFinite(v)) return "—";
  if (v >= 100) return "100% 이상 (이 조성으로는 도달 불가)";
  return `${fmt(v, digits)} vol%`;
}

export function ExplosiveLimitsPanel() {
  const [entries, setEntries] = useState<readonly ComponentEntry[]>(INITIAL_ENTRIES);

  const molePercentSum = entries.reduce((a, e) => a + (num(e.molePercent) || 0), 0);

  const result = useMemo(() => {
    const components = moleFractionsToMassComponents(
      100,
      entries.map((e) => ({ key: e.key, molePercent: num(e.molePercent) })),
    );
    return components === null ? null : mixExplosiveLimits(components);
  }, [entries]);

  const hasUnsupportedInert = entries.some((e) => {
    const c = GAS_COMPONENTS.find((g) => g.key === e.key);
    return c !== undefined && c.lelVolPercent === null && c.nitrogenEquivalent == null && num(e.molePercent) > 0;
  });

  return (
    <section className="card">
      <h2>혼합가스 폭발하한계·상한계 계산기</h2>
      <p className="hint">
        가연성 가스에 불연성 가스가 섞인 혼합물의 폭발하한계(LEL)·폭발상한계(UEL)를 구합니다. 플레어
        계산(다른 탭)과 무관하게, 방폭지역 구분·환기 설계 등 일반적인 혼합가스 조성 검토에 씁니다.
      </p>
      <p className="hint hint--warn">
        ⚠ LEL은 KOSHA GUIDE P-179-2022(ISO 10156 기반) 방식입니다 — 불연성 가스마다 실제 억제력이
        다른 것(질소등가계수: CO2·SO2·HCl은 질소보다 더 억제, Ar·He는 덜 억제)을 보정합니다. UEL은 그
        지침이 다루지 않아, 가연성 성분끼리만 100%로 재정규화한 단순 Le Chatelier 식(불연성 희석
        효과에 대한 보정 없음)으로 구합니다 — 그래서 불연성 가스 비율이 높아도 UEL 값 자체는 잘
        낮아지지 않습니다. 성분별 LEL·UEL·연소열량 값은 NFPA 325 등의 통상 인용값이므로, 안전이 걸린
        정밀 판단에는 실제 유체의 MSDS로 재확인하십시오.
      </p>

      <h3>가스 조성</h3>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>성분</th>
              <th>몰%</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id}>
                <td>
                  <select
                    value={e.key}
                    onChange={(ev) =>
                      setEntries(entries.map((x) => (x.id === e.id ? { ...x, key: ev.target.value } : x)))
                    }
                  >
                    {GAS_COMPONENTS.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.label}
                        {c.lelVolPercent === null
                          ? c.nitrogenEquivalent == null
                            ? " (불연성, 계산 미지원)"
                            : " (불연성)"
                          : ""}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    value={e.molePercent}
                    onChange={(ev) =>
                      setEntries(
                        entries.map((x) => (x.id === e.id ? { ...x, molePercent: ev.target.value } : x)),
                      )
                    }
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    disabled={entries.length <= 1}
                    onClick={() => setEntries(entries.filter((x) => x.id !== e.id))}
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        className="btn btn--ghost"
        onClick={() =>
          setEntries([...entries, { id: `el_${Date.now()}_${entries.length}`, key: "nitrogen", molePercent: "0" }])
        }
      >
        + 성분 추가
      </button>
      <p className={Math.abs(molePercentSum - 100) > 0.5 ? "hint hint--warn" : "hint"}>
        몰% 합계: {fmt(molePercentSum, 1)}% {Math.abs(molePercentSum - 100) > 0.5 && "— 100%에 맞추십시오"}
      </p>

      {result ? (
        <div className="result result--ok">
          <div className="result__headline">
            <span className="result__label">폭발범위</span>
            {result.lelVolPercent >= 100 || result.uelVolPercent === null || result.uelVolPercent >= 100 ? (
              <strong className="result__value">이 조성으로는 도달 불가</strong>
            ) : (
              <>
                <strong className="result__value">
                  {fmt(result.lelVolPercent, 2)} ~ {fmt(result.uelVolPercent, 2)}
                </strong>
                <span className="result__label">vol%</span>
              </>
            )}
          </div>
          <dl className="result__grid">
            <Item label="유도 분자량" value={`${fmt(result.molecularWeight, 3)} kg/kmol`} />
            <Item label="폭발하한계 LEL" value={fmtLimit(result.lelVolPercent)} />
            <Item
              label="폭발상한계 UEL"
              value={result.uelVolPercent === null ? "계산 불가(UEL 데이터 없는 성분 포함)" : fmtLimit(result.uelVolPercent)}
            />
          </dl>
        </div>
      ) : (
        <div className="result result--error" role="alert">
          {hasUnsupportedInert
            ? "질소등가계수를 모르는 불연성 성분(계산 미지원)이 섞여 있어 LEL을 구할 수 없습니다."
            : "가연성 성분이 하나도 없거나 입력이 불완전합니다 — 가연성 성분(불연성 아님)이 하나 이상 있어야 합니다."}
        </div>
      )}
    </section>
  );
}
